/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';
import { type OnboardClinicInput } from '@therapy/shared-validators';

import {
  type AuditEntry,
  type AuditFilters,
  type ClinicDetail,
  type ClinicListItem,
  type CreatedStaffUser,
  type FeatureFlag,
  type SaDashboard,
  type SaStaffUser,
  type SaSubscription,
  type SaUsage,
  saApi,
} from '../api/sa.js';

import type { CreateStaffUserInput } from '@therapy/shared-validators';

const DASHBOARD_KEY = ['sa', 'dashboard'] as const;
const CLINICS_KEY = ['sa', 'clinics'] as const;
const SUBSCRIPTIONS_KEY = ['sa', 'subscriptions'] as const;
const USAGE_KEY = ['sa', 'usage'] as const;
const STAFF_KEY = ['sa', 'staff-users'] as const;
const clinicKey = (id: string): readonly unknown[] => ['sa', 'clinics', id];
const auditKey = (filters: AuditFilters): readonly unknown[] => ['sa', 'audit', filters];
const FLAGS_KEY = ['sa', 'feature-flags'] as const;

export function useSaDashboard(): UseQueryResult<SaDashboard> {
  return useQuery({ queryKey: DASHBOARD_KEY, queryFn: () => saApi.dashboard() });
}

export function useSaClinics(): UseQueryResult<readonly ClinicListItem[]> {
  return useQuery({ queryKey: CLINICS_KEY, queryFn: () => saApi.clinics() });
}

export function useSaSubscriptions(): UseQueryResult<readonly SaSubscription[]> {
  return useQuery({ queryKey: SUBSCRIPTIONS_KEY, queryFn: () => saApi.subscriptions() });
}

export function useMarkSubscriptionPaid(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clinicId: string) => saApi.markSubscriptionPaid(clinicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
    },
  });
}

export function useSaUsage(): UseQueryResult<readonly SaUsage[]> {
  return useQuery({ queryKey: USAGE_KEY, queryFn: () => saApi.usage() });
}

export function useSaStaffUsers(): UseQueryResult<readonly SaStaffUser[]> {
  return useQuery({ queryKey: STAFF_KEY, queryFn: () => saApi.staffUsers() });
}

export function useCreateStaffUser(): UseMutationResult<
  CreatedStaffUser,
  Error,
  CreateStaffUserInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStaffUserInput) => saApi.createStaffUser(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF_KEY });
    },
  });
}

interface SetStaffActiveVars {
  readonly id: string;
  readonly active: boolean;
}

export function useSetStaffActive(): UseMutationResult<SaStaffUser, Error, SetStaffActiveVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: SetStaffActiveVars) => saApi.setStaffActive(vars.id, vars.active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF_KEY });
    },
  });
}

export function useSaClinic(id: string): UseQueryResult<ClinicDetail> {
  return useQuery({ queryKey: clinicKey(id), queryFn: () => saApi.clinic(id) });
}

export function useSaAudit(filters: AuditFilters): UseQueryResult<readonly AuditEntry[]> {
  return useQuery({ queryKey: auditKey(filters), queryFn: () => saApi.audit(filters) });
}

export function useOnboardClinic(): UseMutationResult<ClinicDetail, Error, OnboardClinicInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OnboardClinicInput) => saApi.onboard(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLINICS_KEY });
      void qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
    },
  });
}

export function useFeatureFlags(): UseQueryResult<readonly FeatureFlag[]> {
  return useQuery({ queryKey: FLAGS_KEY, queryFn: () => saApi.featureFlags() });
}

interface SetFlagVars {
  readonly key: string;
  readonly enabled: boolean;
}

export function useSetFeatureFlag(): UseMutationResult<FeatureFlag, Error, SetFlagVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: SetFlagVars) => saApi.setFeatureFlag(vars.key, vars.enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FLAGS_KEY });
    },
  });
}
